import { useState } from "react";
import { useBulkConciliarMutation } from "src/services/conciliacionApi";
import { useSnackbar } from "src/components/snackbar";
import { AlertaModalV2 } from "src/components/AlertaSwall";

// ============================================
// ESTADO INICIAL DE SECCIONES EXPANDIBLES
// ============================================

const INITIAL_EXPANDED_SECTIONS = {
  perfectos: false,
  buenos: false,
  debiles: false,
  sinMatch: false,
  errores: false,
  recienConciliados: true,
};

// ============================================
// ESTADO INICIAL DE PAGINACIÓN
// ============================================

const INITIAL_PAGINATION = {
  perfectos: { page: 0, rowsPerPage: 10 },
  buenos: { page: 0, rowsPerPage: 10 },
  debiles: { page: 0, rowsPerPage: 10 },
  sinMatch: { page: 0, rowsPerPage: 10 },
  errores: { page: 0, rowsPerPage: 10 },
  recienConciliados: { page: 0, rowsPerPage: 10 },
};

// ============================================
// CUSTOM HOOK
// ============================================

export const useResultadosProcesamiento = () => {
  // ==================== ESTADOS ====================

  // Estado de expansión de secciones
  const [expandedSections, setExpandedSections] = useState(
    INITIAL_EXPANDED_SECTIONS,
  );

  // Estado de paginación por sección
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);

  // Estado de loading de acciones (SOLO para "Conciliar Perfectos" - operación masiva)
  const [procesandoAccion, setProcesandoAccion] = useState(false);

  //  Estado de movimientos individuales en proceso
  const [movimientosEnProceso, setMovimientosEnProceso] = useState({});

  //  Estado de movimientos recién conciliados (Array de detalles completos)
  const [recienConciliados, setRecienConciliados] = useState([]);

  // Estado de modal de sugerencias
  const [modalSugerenciasOpen, setModalSugerenciasOpen] = useState({
    movimiento: null,
    open: false,
  });

  // Estado de modal de conciliación manual
  const [modalConciliarOpen, setModalConciliarOpen] = useState({
    movimiento: null,
    open: false,
  });

  // ==================== RTK QUERY ====================

  const [bulkConciliar] = useBulkConciliarMutation();
  const { enqueueSnackbar } = useSnackbar();

  // ==================== HANDLERS DE EXPANSIÓN ====================

  /**
   * Toggle de expansión de una sección
   * @param {String} section - Clave de la sección ('perfectos', 'buenos', etc.)
   */
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  /**
   * Expande una sección específica
   * @param {String} section - Clave de la sección
   */
  const expandSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: true,
    }));
  };

  /**
   * Colapsa una sección específica
   * @param {String} section - Clave de la sección
   */
  const collapseSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: false,
    }));
  };

  /**
   * Colapsa todas las secciones
   */
  const collapseAllSections = () => {
    setExpandedSections(INITIAL_EXPANDED_SECTIONS);
  };

  // ==================== HANDLERS DE PAGINACIÓN ====================

  /**
   * Cambia la página de una sección
   * @param {String} section - Clave de la sección
   * @param {Number} newPage - Nueva página
   */
  const handleChangePage = (section, newPage) => {
    setPagination((prev) => ({
      ...prev,
      [section]: { ...prev[section], page: newPage },
    }));
  };

  /**
   * Cambia las filas por página de una sección
   * @param {String} section - Clave de la sección
   * @param {Event} event - Evento del select
   */
  const handleChangeRowsPerPage = (section, event) => {
    setPagination((prev) => ({
      ...prev,
      [section]: {
        page: 0,
        rowsPerPage: parseInt(event.target.value, 10),
      },
    }));
  };

  /**
   * Resetea la paginación de todas las secciones
   */
  const resetPagination = () => {
    setPagination(INITIAL_PAGINATION);
  };

  // ==================== HANDLERS DE MODALES AUXILIARES ====================

  /**
   * Abre el modal de sugerencias para un movimiento
   * @param {Object} movimiento - Objeto del movimiento
   */
  const handleVerSugerencias = (movimiento) => {
    setModalSugerenciasOpen({
      movimiento: movimiento,
      open: true,
    });
  };

  /**
   * Cierra el modal de sugerencias
   */
  const handleCloseSugerencias = () => {
    setModalSugerenciasOpen({
      movimiento: null,
      open: false,
    });
  };

  /**
   * Abre el modal de conciliación manual para un movimiento
   * @param {Object} movimiento - Objeto del movimiento
   */
  const handleConciliarManual = (movimiento) => {
    setModalConciliarOpen({
      movimiento: movimiento,
      open: true,
    });
  };

  /**
   * Cierra el modal de conciliación manual
   */
  const handleCloseConciliarManual = () => {
    setModalConciliarOpen({
      movimiento: null,
      open: false,
    });
  };

  // ==================== HANDLERS DE ACCIONES DE CONCILIACIÓN ====================

  /**
   * Concilia todos los movimientos perfectos (modo marcha blanca)
   * @param {Array} clasificados - Objeto con movimientos clasificados
   * @param {Object} porCategoria - Métricas por categoría
   * @param {Function} onClose - Callback para cerrar el modal al finalizar
   */
  const handleConciliarPerfectos = async (
    clasificados,
    porCategoria,
    onClose,
  ) => {
    if (clasificados.perfectos.length === 0) {
      enqueueSnackbar("No hay movimientos perfectos para conciliar", {
        variant: "warning",
      });
      return;
    }

    // Confirmación del usuario
    const resultado = await AlertaModalV2({
      title: `¿Conciliar automáticamente ${clasificados.perfectos.length} movimientos perfectos?`,
      confirmButtonText: "Sí, Conciliar",
      confirmButtonColor: "#4caf50",
      cancelButtonText: "Cancelar",
      width: 550,
    }).fire({
      html: `
    <div style="text-align: left; padding: 20px 0;">
      <p style="margin-bottom: 12px; color: #666;">
        <strong>Movimientos:</strong> ${clasificados.perfectos.length}
      </p>
      <p style="margin-bottom: 12px; color: #666;">
        <strong>Monto total:</strong> $${(
          porCategoria.perfectos.monto / 1000000
        ).toFixed(2)}M
      </p>
      <p style="margin-bottom: 0; color: #666;">
        Esta acción conciliará todos los movimientos con score ≥90 puntos.
      </p>
    </div>
  `,
    });

    if (!resultado.isConfirmed) return;

    setProcesandoAccion(true);

    try {
      // Preparar operaciones para bulk conciliar
      const operaciones = clasificados.perfectos.map((detalle) => ({
        movimiento_id: detalle.movimiento_id,
        facturas: detalle.facturas.map((f) => ({
          tipo: f.tipo,
          id: f.id,
          monto_asignado: f.monto,
        })),
        observaciones: `Auto-conciliación desde modal de resultados (Score: ${detalle.score})`,
      }));

      const resultado = await bulkConciliar({
        operaciones,
        observaciones_generales:
          "Conciliación masiva de matches perfectos desde modal de resultados",
      }).unwrap();

      const exitosos = resultado.resultados?.exitosos?.length || 0;
      const fallidos = resultado.resultados?.fallidos?.length || 0;

      if (exitosos > 0) {
        enqueueSnackbar(
          `✅ ${exitosos} movimiento${exitosos > 1 ? "s" : ""} conciliado${
            exitosos > 1 ? "s" : ""
          } exitosamente`,
          { variant: "success" },
        );
      }

      if (fallidos > 0) {
        enqueueSnackbar(
          `⚠️ ${fallidos} movimiento${fallidos > 1 ? "s" : ""} no pudo${
            fallidos > 1 ? "ron" : ""
          } ser conciliado${fallidos > 1 ? "s" : ""}`,
          { variant: "warning" },
        );
      }

      // Cerrar modal después de 1.5 segundos si todo fue exitoso
      if (exitosos > 0 && fallidos === 0) {
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (error) {
      console.error("Error al conciliar perfectos:", error);
      enqueueSnackbar(
        error?.data?.message || "Error al conciliar movimientos",
        { variant: "error" },
      );
    } finally {
      setProcesandoAccion(false);
    }
  };

  /**
   * Concilia un movimiento individual desde la tabla
   *
   * @param {Object} detalle - Detalle del movimiento a conciliar
   */
  const handleConciliarIndividual = async (detalle) => {
    if (!detalle.facturas || detalle.facturas.length === 0) {
      enqueueSnackbar("El movimiento no tiene facturas asociadas", {
        variant: "warning",
      });
      return;
    }

    // Confirmación del usuario
    const resultado = await AlertaModalV2({
      title: `¿Conciliar el movimiento #${detalle.movimiento_id}?`,
      confirmButtonText: "Sí, Conciliar",
      confirmButtonColor: "#4caf50",
      cancelButtonText: "Cancelar",
      width: 500,
    }).fire({
      html: `
    <div style="text-align: left; padding: 20px 0;">
      <p style="margin-bottom: 12px; color: #666;">
        <strong>Monto:</strong> $${
          detalle.movimiento?.monto?.toLocaleString("es-CL") || 0
        }
      </p>
      <p style="margin-bottom: 12px; color: #666;">
        <strong>Facturas:</strong> ${detalle.facturas
          .map((f) => `${f.tipo} #${f.folio}`)
          .join(", ")}
      </p>
      <p style="margin-bottom: 0; color: #666;">
        <strong>Score:</strong> ${detalle.score} puntos
      </p>
    </div>
  `,
    });

    if (!resultado.isConfirmed) return;

    // Agregar ID al objeto de movimientos en proceso
    setMovimientosEnProceso((prev) => ({
      ...prev,
      [String(detalle.movimiento_id)]: true,
    }));

    try {
      const operaciones = [
        {
          movimiento_id: detalle.movimiento_id,
          facturas: detalle.facturas.map((f) => ({
            tipo: f.tipo,
            id: f.id,
            monto_asignado: f.monto,
          })),
          observaciones: `Conciliación individual desde modal de resultados (Score: ${detalle.score})`,
        },
      ];

      await bulkConciliar({
        operaciones,
      }).unwrap();

      // Mover a recién conciliados (con timestamp para ordenar)
      setRecienConciliados((prev) => [
        { ...detalle, conciliadoEn: new Date().toISOString() },
        ...prev,
      ]);

      // Mostrar snackbar de éxito
      enqueueSnackbar(
        `✅ Movimiento #${detalle.movimiento_id} conciliado exitosamente`,
        { variant: "success" },
      );
    } catch (error) {
      console.error("Error al conciliar:", error);
      enqueueSnackbar(error?.data?.message || "Error al conciliar movimiento", {
        variant: "error",
      });
    } finally {
      //  Quitar del Set de movimientos en proceso
      setMovimientosEnProceso((prev) => {
        const { [String(detalle.movimiento_id)]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  /**
   * Handler para cuando se concilia desde modales anidados (SugerenciasModal, ConciliacionManualModal)
   *
   * @param {Object} detalleConciliado - Detalle del movimiento que fue conciliado
   */
  const handleConciliacionExitosaDesdeModal = (detalleConciliado) => {
    // Agregar a recién conciliados
    setRecienConciliados((prev) => [detalleConciliado, ...prev]);

    // Cerrar modal de sugerencias si está abierto
    setModalSugerenciasOpen({ movimiento: null, open: false });

    // Cerrar modal de conciliación manual si está abierto
    setModalConciliarOpen({ movimiento: null, open: false });
  };

  /**
   * Resetea todo el estado del hook
   */
  const resetState = () => {
    setExpandedSections(INITIAL_EXPANDED_SECTIONS);
    setPagination(INITIAL_PAGINATION);
    setProcesandoAccion(false);
    setMovimientosEnProceso({});
    setRecienConciliados([]);
    setModalSugerenciasOpen({ movimiento: null, open: false });
    setModalConciliarOpen({ movimiento: null, open: false });
  };

  return {
    // Estados
    expandedSections,
    pagination,
    procesandoAccion,
    movimientosEnProceso,
    recienConciliados,
    modalSugerenciasOpen,
    modalConciliarOpen,

    // Handlers de expansión
    toggleSection,
    expandSection,
    collapseSection,
    collapseAllSections,

    // Handlers de paginación
    handleChangePage,
    handleChangeRowsPerPage,
    resetPagination,

    // Handlers de modales auxiliares
    handleVerSugerencias,
    handleCloseSugerencias,
    handleConciliarManual,
    handleCloseConciliarManual,

    // Handlers de acciones
    handleConciliarPerfectos,
    handleConciliarIndividual,
    handleConciliacionExitosaDesdeModal,

    // Reset
    resetState,
  };
};
